import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  eventHiddenFromFamilies,
  events as seedEvents,
  formatDateLabel,
  seedRegistrations,
  seedSessions,
  sessionKey,
  staff,
  type AgeFilter,
  type EventAttendanceSession,
  type EventKind,
  type EventSessionRow,
  type EventStatus,
  type LegacyAttendanceRow,
  type LiftEvent,
  type Registration,
  type School,
} from "../domain/data";
import {
  cancelRegistration as cancelRegistrationRecord,
  checkInByCode as checkInByCodeRecord,
  checkInRegistration as checkInRegistrationRecord,
  eventCounts,
  registerStudent as registerStudentRecord,
  type CheckInResult,
  type EventCounts,
  type RegisterResult,
  undoCheckIn as undoCheckInRecord,
  upsertAttendanceSession,
  withRegisteredCount,
  migrateAttendanceToRegistrations,
  activeRegistration,
} from "../domain/registrations";
import { omitStudentId } from "../domain/roster";
import {
  mergeQuestionTranslation,
  normalizeQuestion,
  type FormQuestion,
} from "../domain/questions";
import { emitRegistrationNotice, type RegistrationNotice } from "../domain/email/notices";
import {
  loadEventsBundle,
  onRemoteSession,
  remoteClient,
  replaceEventQuestions,
  saveAttendance,
  saveEvent,
  saveEventStatus,
  saveRegistration,
  subscribeChanges,
  type RemoteCounts,
} from "../data/remote";

const STORAGE_KEY = "lift-app-events-v6";
const LEGACY_STORAGE_KEY = "lift-app-events-v5";
const RETIRED_EVENT_IDS = new Set(["bowling-night", "glen-lake-game-night"]);

export type CreateEventInput = {
  name: string;
  dateIso: string;
  timeLabel: string;
  location: string;
  capacity: number;
  schools: School[] | "all";
  age: AgeFilter;
  kind: EventKind;
  questions?: FormQuestion[];
  description?: string;
  descriptionEs?: string;
};

type Stored = {
  events: LiftEvent[];
  registrations: Record<string, Registration[]>;
  sessions: Record<string, EventAttendanceSession>;
};

type LegacyStored = {
  events?: LiftEvent[];
  attendance?: Record<string, LegacyAttendanceRow[]>;
  registrations?: Record<string, Registration[]>;
  sessions?: Record<string, EventAttendanceSession>;
};

type EventsContextValue = {
  events: LiftEvent[];
  upcomingEvents: LiftEvent[];
  pastEvents: LiftEvent[];
  programEvents: LiftEvent[];
  getEvent: (id: string) => LiftEvent | undefined;
  registrationsFor: (eventId: string) => Registration[];
  countsFor: (eventId: string) => EventCounts;
  sessionsFor: (eventId: string) => EventAttendanceSession[];
  sessionFor: (
    eventId: string,
    dateIso: string,
  ) => EventAttendanceSession | undefined;
  saveSession: (
    eventId: string,
    dateIso: string,
    rows: EventSessionRow[],
  ) => EventAttendanceSession;
  addEvent: (input: CreateEventInput) => LiftEvent;
  updateEvent: (
    eventId: string,
    patch: { questions: FormQuestion[] },
  ) => void;
  archiveEvent: (eventId: string) => void;
  restoreEvent: (eventId: string) => void;
  registerStudent: (
    eventId: string,
    studentId: string,
    answers?: Record<string, string>,
  ) => RegisterResult;
  cancelRegistration: (eventId: string, studentId: string) => boolean;
  checkInStudent: (eventId: string, studentId: string) => CheckInResult;
  checkInByCode: (eventId: string, code: string) => CheckInResult;
  undoCheckIn: (eventId: string, registrationId: string) => boolean;
  registrationForStudent: (
    eventId: string,
    studentId: string,
  ) => Registration | undefined;
  forgetStudent: (studentId: string) => void;
};

const EventsContext = createContext<EventsContextValue | null>(null);

function registrationNotice(
  event: Pick<LiftEvent, "id" | "name" | "dateLabel" | "timeLabel" | "location">,
  studentId: string,
  status: "registered" | "waitlisted",
  promoted = false,
): RegistrationNotice {
  return {
    studentId,
    eventId: event.id,
    eventName: event.name,
    dateLabel: event.dateLabel,
    timeLabel: event.timeLabel,
    location: event.location,
    status,
    promoted,
  };
}

function emitOpenRegistration(
  event: Pick<LiftEvent, "id" | "name" | "dateLabel" | "timeLabel" | "location">,
  studentId: string,
  status: Registration["status"],
  promoted = false,
): void {
  if (status !== "registered" && status !== "waitlisted") return;
  emitRegistrationNotice(registrationNotice(event, studentId, status, promoted));
}

function emptyRemote(): Stored {
  return { events: [], registrations: {}, sessions: {} };
}

function cloneSeed(): Stored {
  return {
    events: seedEvents.map((event) => ({ ...event })),
    registrations: Object.fromEntries(
      Object.entries(seedRegistrations).map(([id, rows]) => [
        id,
        rows.map((row) => ({ ...row })),
      ]),
    ),
    sessions: Object.fromEntries(
      seedSessions.map((session) => [
        session.id,
        { ...session, rows: session.rows.map((row) => ({ ...row })) },
      ]),
    ),
  };
}

function normalizeAge(event: { id: string; age?: AgeFilter }): AgeFilter {
  if (event.age === "ms" || event.age === "hs" || event.age === "all") {
    return event.age;
  }
  return seedEvents.find((item) => item.id === event.id)?.age ?? "all";
}

function normalizeEvent(event: LiftEvent): LiftEvent {
  const kind = event.kind ?? "rsvp";
  return {
    id: event.id,
    slug: event.slug ?? event.id,
    name: event.name,
    dateIso: event.dateIso ?? "",
    dateLabel: event.dateLabel || (event.dateIso ? formatDateLabel(event.dateIso) : ""),
    timeLabel: event.timeLabel,
    location: event.location,
    capacity: event.capacity,
    registered: event.registered ?? 0,
    schools: event.schools,
    age: normalizeAge(event),
    status: event.status ?? "open",
    kind,
    waitlistEnabled: event.waitlistEnabled ?? kind !== "program",
    questions: mergeEventQuestions(event.id, event.questions),
    description: event.description ?? fallbackDescription(event.id, "en"),
    descriptionEs: event.descriptionEs ?? fallbackDescription(event.id, "es"),
  };
}

function fallbackDescription(eventId: string, locale: "en" | "es"): string {
  const seed = seedEvents.find((event) => event.id === eventId);
  if (!seed) return "";
  return locale === "es" ? seed.descriptionEs : seed.description;
}

function fallbackQuestions(eventId: string): FormQuestion[] {
  return seedEvents.find((event) => event.id === eventId)?.questions ?? [];
}

function mergeEventQuestions(
  eventId: string,
  stored: FormQuestion[] | undefined,
): FormQuestion[] {
  const seed = fallbackQuestions(eventId);
  const source = stored && stored.length > 0 ? stored : seed;
  return source.map((item) =>
    mergeQuestionTranslation(
      normalizeQuestion(item),
      seed.find((row) => row.id === item.id),
    ),
  );
}

function cloneRegistrations(
  registrations: Record<string, Registration[]>,
): Record<string, Registration[]> {
  return Object.fromEntries(
    Object.entries(registrations).map(([id, rows]) => [
      id,
      rows.map((row) => ({ ...row, answers: row.answers ?? {} })),
    ]),
  );
}

function parseStored(raw: string): Stored | null {
  const parsed = JSON.parse(raw) as LegacyStored;
  if (!parsed || !Array.isArray(parsed.events)) return null;
  const events = parsed.events.map(normalizeEvent);
  const registrations = parsed.registrations
    ? cloneRegistrations(parsed.registrations)
    : migrateAttendanceToRegistrations(parsed.attendance ?? {});
  const synced = events.map((event) =>
    withRegisteredCount(event, registrations[event.id] ?? []),
  );
  return {
    events: synced,
    registrations,
    sessions: parsed.sessions ?? {},
  };
}

function withoutRetired(store: Stored): Stored {
  const events = store.events.filter((event) => !RETIRED_EVENT_IDS.has(event.id));
  if (events.length === store.events.length) return store;
  const registrations = Object.fromEntries(
    Object.entries(store.registrations).filter(([id]) => !RETIRED_EVENT_IDS.has(id)),
  );
  const sessions = Object.fromEntries(
    Object.entries(store.sessions).filter(([key, session]) => {
      const eventId = session.eventId || key.split("::")[0] || key;
      return !RETIRED_EVENT_IDS.has(eventId);
    }),
  );
  return { events, registrations, sessions };
}

function readStore(): Stored {
  if (typeof window === "undefined") return cloneSeed();
  try {
    const current = window.localStorage.getItem(STORAGE_KEY);
    if (current) {
      const stored = parseStored(current);
      if (!stored) return cloneSeed();
      const cleaned = withoutRetired(stored);
      if (cleaned !== stored) persist(cleaned);
      return cleaned;
    }
    const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (legacy) {
      const migrated = parseStored(legacy);
      if (migrated) {
        const cleaned = withoutRetired(migrated);
        persist(cleaned);
        return cleaned;
      }
    }
    return cloneSeed();
  } catch {
    return cloneSeed();
  }
}

function persist(store: Stored) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / private mode */
  }
}

function slugify(name: string): string {
  const base =
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "event";
  return `${base}-${Date.now().toString(36)}`;
}

function nowLabel(): string {
  return new Date().toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function nowIso(): string {
  return new Date().toISOString();
}

function savedAtLabel(): string {
  return new Date().toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function splitEvents(events: LiftEvent[]) {
  const upcoming: LiftEvent[] = [];
  const past: LiftEvent[] = [];
  const programs: LiftEvent[] = [];
  events.forEach((event) => {
    if (event.kind === "program") programs.push(event);
    else if (eventHiddenFromFamilies(event)) past.push(event);
    else upcoming.push(event);
  });
  upcoming.sort((a, b) => a.dateIso.localeCompare(b.dateIso));
  past.sort((a, b) => b.dateIso.localeCompare(a.dateIso));
  programs.sort((a, b) => a.name.localeCompare(b.name));
  return { upcoming, past, programs };
}

function listSessions(
  sessions: Record<string, EventAttendanceSession>,
  eventId: string,
): EventAttendanceSession[] {
  return Object.values(sessions)
    .filter((session) => session.eventId === eventId)
    .sort((a, b) => b.dateIso.localeCompare(a.dateIso));
}

function syncEvent(
  store: Stored,
  eventId: string,
  registrations: Registration[],
): Stored {
  return {
    ...store,
    events: store.events.map((event) =>
      event.id === eventId ? withRegisteredCount(event, registrations) : event,
    ),
    registrations: { ...store.registrations, [eventId]: registrations },
  };
}

function withServerCounts(events: LiftEvent[], counts: RemoteCounts): LiftEvent[] {
  return events.map((event) => {
    const registered = counts[event.id]?.registered;
    return registered === undefined ? event : { ...event, registered };
  });
}

export function EventsProvider({ children }: { children: ReactNode }) {
  const [store, setStore] = useState<Stored>(() =>
    remoteClient() ? emptyRemote() : readStore(),
  );
  const [counts, setCounts] = useState<RemoteCounts>({});
  const storeRef = useRef(store);
  const countsRef = useRef(counts);
  storeRef.current = store;
  countsRef.current = counts;

  const write = useCallback((next: Stored) => {
    storeRef.current = next;
    setStore(next);
    if (!remoteClient()) persist(next);
  }, []);

  const loadGen = useRef(0);

  const reload = useCallback(async () => {
    const client = remoteClient();
    if (!client) return;
    const token = ++loadGen.current;
    try {
      const bundle = await loadEventsBundle(client);
      if (token !== loadGen.current) return;
      const next = {
        events: withServerCounts(bundle.events, bundle.counts),
        registrations: bundle.registrations,
        sessions: bundle.sessions,
      };
      storeRef.current = next;
      countsRef.current = bundle.counts;
      setStore(next);
      setCounts(bundle.counts);
    } catch {
      /* keep the last snapshot */
    }
  }, []);

  useEffect(() => {
    const client = remoteClient();
    if (!client) return;
    const stopSession = onRemoteSession(client, () => {
      void reload();
    });
    const stop = subscribeChanges(
      client,
      ["events", "event_questions", "registrations", "attendance_sessions", "attendance_marks"],
      () => {
        void reload();
      },
    );
    const onFocus = () => {
      if (document.visibilityState === "visible") void reload();
    };
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      stopSession();
      stop();
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [reload]);

  const addEvent = useCallback(
    (input: CreateEventInput) => {
      const current = storeRef.current;
      const id = remoteClient() ? crypto.randomUUID() : slugify(input.name);
      const event: LiftEvent = {
        id,
        slug: id,
        name: input.name,
        dateIso: input.dateIso,
        dateLabel: formatDateLabel(input.dateIso),
        timeLabel: input.timeLabel,
        location: input.location,
        capacity: input.capacity,
        registered: 0,
        schools: input.schools,
        age: input.age,
        status: "open",
        kind: input.kind,
        waitlistEnabled: input.kind !== "program",
        questions: (input.questions ?? []).map((item) =>
          normalizeQuestion(item),
        ),
        description: input.description?.trim() ?? "",
        descriptionEs: input.descriptionEs?.trim() ?? "",
      };
      write({
        events: [...current.events, event],
        registrations: { ...current.registrations, [id]: [] },
        sessions: current.sessions,
      });
      const client = remoteClient();
      if (client) void saveEvent(client, event).then(() => reload()).catch(() => reload());
      return event;
    },
    [reload, write],
  );

  const updateEvent = useCallback(
    (eventId: string, patch: { questions: FormQuestion[] }) => {
      const current = storeRef.current;
      write({
        ...current,
        events: current.events.map((event) =>
          event.id === eventId
            ? {
                ...event,
                questions: patch.questions.map((item) =>
                  normalizeQuestion(item),
                ),
              }
            : event,
        ),
      });
      const client = remoteClient();
      if (client) {
        void replaceEventQuestions(client, eventId, patch.questions)
          .then(() => reload())
          .catch(() => reload());
      }
    },
    [reload, write],
  );

  const setStatus = useCallback(
    (eventId: string, status: EventStatus) => {
      const current = storeRef.current;
      write({
        ...current,
        events: current.events.map((event) =>
          event.id === eventId ? { ...event, status } : event,
        ),
      });
      const client = remoteClient();
      if (client) {
        void saveEventStatus(client, eventId, status).then(() => reload()).catch(() => reload());
      }
    },
    [reload, write],
  );

  const registerStudent = useCallback(
    (
      eventId: string,
      studentId: string,
      answers: Record<string, string> = {},
    ): RegisterResult => {
      const current = storeRef.current;
      const event = current.events.find((item) => item.id === eventId);
      if (!event) {
        return { registration: null, reason: "event_closed", created: false };
      }
      const { registrations, result } = registerStudentRecord(
        event,
        current.registrations[eventId] ?? [],
        studentId,
        nowIso(),
        answers,
        remoteClient() ? countsRef.current[eventId]?.registered : undefined,
      );
      if (result.created) write(syncEvent(current, eventId, registrations));
      const client = remoteClient();
      if (result.created && result.registration) {
        const created = result.registration;
        if (!client) {
          emitOpenRegistration(event, studentId, created.status);
        } else {
          void saveRegistration(client, created, "family")
            .then((saved) => {
              emitOpenRegistration(event, studentId, saved.status);
              return reload();
            })
            .catch(() => reload());
        }
      }
      return result;
    },
    [reload, write],
  );

  const cancelRegistration = useCallback(
    (eventId: string, studentId: string) => {
      const current = storeRef.current;
      const event = current.events.find((item) => item.id === eventId);
      if (!event) return false;
      const { registrations, cancelled } = cancelRegistrationRecord(
        event,
        current.registrations[eventId] ?? [],
        studentId,
        nowIso(),
      );
      if (!cancelled) return false;
      write(syncEvent(current, eventId, registrations));
      const before = current.registrations[eventId] ?? [];
      const client = remoteClient();
      if (!client) {
        registrations.forEach((row) => {
          if (row.id === cancelled.id) return;
          const previous = before.find((item) => item.id === row.id);
          if (previous && previous.status !== row.status) {
            emitOpenRegistration(
              event,
              row.studentId,
              row.status,
              previous.status === "waitlisted" && row.status === "registered",
            );
          }
        });
        return true;
      }
      void saveRegistration(client, cancelled, "family")
        .then(async () => {
          const bundle = await loadEventsBundle(client);
          (bundle.registrations[eventId] ?? []).forEach((row) => {
            if (row.id === cancelled.id) return;
            const previous = before.find((item) => item.id === row.id);
            if (previous && previous.status !== row.status) {
              emitOpenRegistration(
                event,
                row.studentId,
                row.status,
                previous.status === "waitlisted" && row.status === "registered",
              );
            }
          });
          await reload();
        })
        .catch(() => reload());
      return true;
    },
    [reload, write],
  );

  const checkInStudent = useCallback(
    (eventId: string, studentId: string): CheckInResult => {
      const current = storeRef.current;
      const event = current.events.find((item) => item.id === eventId);
      if (!event) return "event_closed";
      const existing = current.registrations[eventId] ?? [];
      const registration = activeRegistration(existing, studentId);
      if (!registration) return "not_registered";
      const { registrations, result } = checkInRegistrationRecord(
        event,
        existing,
        registration.id,
        staff.id,
        nowLabel(),
      );
      if (result === "checked_in") {
        write(syncEvent(current, eventId, registrations));
        const saved = registrations.find((row) => row.id === registration.id);
        const client = remoteClient();
        if (client && saved) {
          void saveRegistration(client, saved, "check-in")
            .then(() => reload())
            .catch(() => reload());
        }
      }
      return result;
    },
    [reload, write],
  );

  const checkInByCode = useCallback(
    (eventId: string, code: string): CheckInResult => {
      const current = storeRef.current;
      const event = current.events.find((item) => item.id === eventId);
      if (!event) return "event_closed";
      const { registrations, result } = checkInByCodeRecord(
        event,
        current.registrations[eventId] ?? [],
        code,
        staff.id,
        nowLabel(),
      );
      if (result === "checked_in") {
        write(syncEvent(current, eventId, registrations));
        const previous = current.registrations[eventId] ?? [];
        const saved = registrations.find((row) => {
          const before = previous.find((item) => item.id === row.id);
          return Boolean(row.checkedInAt) && before?.checkedInAt !== row.checkedInAt;
        });
        const client = remoteClient();
        if (client && saved) {
          void saveRegistration(client, saved, "check-in")
            .then(() => reload())
            .catch(() => reload());
        }
      }
      return result;
    },
    [reload, write],
  );

  const undoCheckIn = useCallback(
    (eventId: string, registrationId: string) => {
      const current = storeRef.current;
      const { registrations, undone } = undoCheckInRecord(
        current.registrations[eventId] ?? [],
        registrationId,
      );
      if (!undone) return false;
      write(syncEvent(current, eventId, registrations));
      const cleared = registrations.find((row) => row.id === registrationId);
      const client = remoteClient();
      if (client && cleared) {
        void saveRegistration(client, cleared, "clear-check-in")
          .then(() => reload())
          .catch(() => reload());
      }
      return true;
    },
    [reload, write],
  );

  const saveSession = useCallback(
    (eventId: string, dateIso: string, rows: EventSessionRow[]) => {
      const current = storeRef.current;
      const { sessions, session } = upsertAttendanceSession(
        current.sessions,
        eventId,
        dateIso,
        rows,
        savedAtLabel(),
      );
      write({ ...current, sessions });
      const client = remoteClient();
      if (client) {
        void saveAttendance(client, session).then(() => reload()).catch(() => reload());
      }
      return session;
    },
    [reload, write],
  );

  const forgetStudent = useCallback(
    (studentId: string) => {
      const current = storeRef.current;
      const registrations = Object.fromEntries(
        Object.entries(current.registrations).map(([eventId, rows]) => [
          eventId,
          omitStudentId(rows, studentId),
        ]),
      );
      const sessions = Object.fromEntries(
        Object.entries(current.sessions).map(([key, session]) => [
          key,
          {
            ...session,
            rows: omitStudentId(session.rows, studentId),
          },
        ]),
      );
      write({
        events: current.events.map((event) =>
          withRegisteredCount(event, registrations[event.id] ?? []),
        ),
        registrations,
        sessions,
      });
      if (remoteClient()) void reload();
    },
    [reload, write],
  );

  const value = useMemo<EventsContextValue>(() => {
    const { upcoming, past, programs } = splitEvents(store.events);
    return {
      events: store.events,
      upcomingEvents: upcoming,
      pastEvents: past,
      programEvents: programs,
      getEvent: (id) => store.events.find((event) => event.id === id),
      registrationsFor: (eventId) => store.registrations[eventId] ?? [],
      countsFor: (eventId) => {
        const event = store.events.find((item) => item.id === eventId);
        if (!event) {
          return {
            registered: 0,
            waitlisted: 0,
            checkedIn: 0,
            remaining: 0,
            cancelled: 0,
          };
        }
        const server = counts[eventId];
        if (server) {
          return {
            registered: server.registered,
            waitlisted: server.waitlisted,
            checkedIn: server.checkedIn,
            remaining: Math.max(0, event.capacity - server.registered),
            cancelled: server.cancelled,
          };
        }
        return eventCounts(event, store.registrations[eventId] ?? []);
      },
      sessionsFor: (eventId) => listSessions(store.sessions, eventId),
      sessionFor: (eventId, dateIso) =>
        store.sessions[sessionKey(eventId, dateIso)],
      saveSession,
      addEvent,
      updateEvent,
      archiveEvent: (eventId) => setStatus(eventId, "archived"),
      restoreEvent: (eventId) => setStatus(eventId, "open"),
      registerStudent,
      cancelRegistration,
      checkInStudent,
      checkInByCode,
      undoCheckIn,
      registrationForStudent: (eventId, studentId) =>
        activeRegistration(store.registrations[eventId] ?? [], studentId),
      forgetStudent,
    };
  }, [
    counts,
    store,
    addEvent,
    updateEvent,
    setStatus,
    registerStudent,
    cancelRegistration,
    checkInStudent,
    checkInByCode,
    undoCheckIn,
    saveSession,
    forgetStudent,
  ]);

  return (
    <EventsContext.Provider value={value}>{children}</EventsContext.Provider>
  );
}

export function useEvents(): EventsContextValue {
  const ctx = useContext(EventsContext);
  if (!ctx) throw new Error("useEvents must be used within EventsProvider");
  return ctx;
}

export type { EventSessionRow, SessionMark } from "../domain/data";
export type { CheckInResult, EventCounts, RegisterResult };
